// src/services/PDFGeneratorService.js

import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { formatTime } from '../models/TimeEntryReportModel';

// ─── Duration formatters ───────────────────────────────────────────────────────

// Time tracking entries store durations in seconds
const formatDuration = (duration) => {
  const roundedDurationInSeconds = Math.round(duration || 0);
  const hours = Math.floor(roundedDurationInSeconds / 3600);
  const minutes = Math.floor((roundedDurationInSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

// Schedule entries store durations in minutes
const formatMinutes = (totalMinutes) => {
  const mins = Math.round(totalMinutes || 0);
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return `${hours}h ${minutes}m`;
};

// ─── Date formatters ───────────────────────────────────────────────────────────

// Converts a YYYY-MM-DD string to DD.MM.YY for PDF display
const formatDateFromString = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0].slice(-2)}`;
};

// ─── Category inference ────────────────────────────────────────────────────────

const inferCategory = (reportType) => {
  if (reportType === 'Vacation Overview' || reportType === 'Vacation Summary') {
    return 'vacation';
  }
  if (reportType === 'Schedule Overview' || reportType === 'Weekly Summary') {
    return 'schedule';
  }
  if (reportType === 'Sick Leave Overview') {
    return 'sick-leave';
  }
  return 'time-tracking';
};

// ─── PDF Styles ────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  page: {
    padding: 40,
    flexDirection: 'column',
    backgroundColor: '#FFFFFF'
  },
  header: {
    marginBottom: 20,
  },

  // Replaced single `title` style with two separate styles so company
  // name and report type each occupy their own line and never wrap together.
  companyTitle: {
    fontSize: 13,
    marginBottom: 4,
    color: '#333333',
  },
  reportTypeTitle: {
    fontSize: 22,
    marginBottom: 10,
    color: '#000000',
  },

  staffName: {
    fontSize: 18,
    marginBottom: 5,
    color: '#0066CC'
  },
  departmentLine: {
    fontSize: 12,
    marginBottom: 15,
  },
  section: {
    marginBottom: 10,
    flexDirection: 'row',
  },
  sectionLabel: {
    fontSize: 12,
    width: 120,
    color: '#0066CC'
  },
  sectionValue: {
    fontSize: 12,
    flexGrow: 1,
  },
  table: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#CCCCCC',
    borderStyle: 'solid',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#F5F5F5',
    borderBottomWidth: 1,
    borderBottomColor: '#CCCCCC',
    borderBottomStyle: 'solid',
    padding: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#EEEEEE',
    borderBottomStyle: 'solid',
    padding: 8,
  },
  alternateRow: {
    backgroundColor: '#F9F9F9',
  },
  totalRow: {
    flexDirection: 'row',
    borderTopWidth: 2,
    borderTopColor: '#000000',
    borderTopStyle: 'solid',
    backgroundColor: '#F5F5F5',
    padding: 8,
    fontWeight: 'bold',
  },
  cell: {
    fontSize: 10,
  },
  headerCell: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#333333',
  },
  totalCell: {
    fontSize: 11,
    fontWeight: 'bold',
  },

  // ── Time tracking column widths ──────────────────────────────────────────────
  dateCell:             { width: '16%' },
  staffCell:            { width: '28%', paddingRight: 8 },
  presenceDurationCell: { width: '19%' },
  breakTimeCell:        { width: '19%' },
  workDurationCell:     { width: '18%' },

  // ── Working Time Record column widths (7 columns, sum = 100%) ────────────────
  // Sized for the 11pt bold header words: "Anwesenheit" is about 68pt wide, so its
  // column must be wider than 68pt (15% of the ~497pt usable table width is ~75pt)
  // or the header wraps / touches the neighbouring column.
  wtrDateCell:          { width: '12%' },
  wtrStaffCell:         { width: '24%', paddingRight: 8 },
  wtrTimeCell:          { width: '10%' },
  wtrPresenceCell:      { width: '15%' },
  wtrBreakCell:         { width: '14%' },
  wtrWorkCell:          { width: '15%' },

  // ── Vacation Overview column widths ──────────────────────────────────────────
  vacDateCell:          { width: '30%' },
  vacStaffCell:         { width: '70%', paddingRight: 8 },

  // ── Vacation Summary column widths ────────────────────────────────────────────
  vacSummStaffCell:     { width: '65%', paddingRight: 8 },
  vacSummDaysCell:      { width: '35%' },

  // ── Schedule Overview column widths ──────────────────────────────────────────
  schedDateCell:        { width: '13%' },
  schedStaffCell:       { width: '22%', paddingRight: 8 },
  schedStartCell:       { width: '15%' },
  schedEndCell:         { width: '15%' },
  schedHoursCell:       { width: '35%' },

  // ── Weekly Summary column widths ─────────────────────────────────────────────
  weekLabelCell:        { width: '20%' },
  weekStaffCell:        { width: '40%', paddingRight: 8 },
  weekHoursCell:        { width: '40%' },

  // Sick Leave Overview reuses the same 2-column proportions as
  // Vacation Overview. No new style entries are required — vacDateCell and
  // vacStaffCell are referenced directly in getColumnConfigs below.

  divider: {
    marginTop: 20,
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#CCCCCC',
    borderBottomStyle: 'solid',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 9,
    color: '#666666',
    paddingTop: 10,
    borderTopWidth: 0.5,
    borderTopColor: '#CCCCCC',
    borderTopStyle: 'solid',
  },
  companyInfo: {
    fontSize: 8,
    color: '#999999',
    marginTop: 5,
  },
  pageNumber: {
    position: 'absolute',
    top: 30,
    right: 40,
    fontSize: 10,
    color: '#666666',
  }
});

// ─── PDFGeneratorService ───────────────────────────────────────────────────────

class PDFGeneratorService {

  // ─── Data grouping helpers ─────────────────────────────────────────────────

  /**
   * Groups flat vacation data by staff member for the Vacation Summary report.
   * Returns one row per staff with totalDays only.
   * durationMinutes is not tracked - vacation reports show day counts only.
   */
  _groupVacationByStaff(data) {
    const grouped = {};
    data.forEach(entry => {
      const key = entry.staffId || entry.staffName || 'unknown';
      if (!grouped[key]) {
        grouped[key] = {
          staffName: entry.staffName || '',
          totalDays: 0
        };
      }
      grouped[key].totalDays += 1;
    });
    return Object.values(grouped).sort((a, b) =>
      (a.staffName || '').localeCompare(b.staffName || '')
    );
  }

  /**
   * Groups flat schedule data by ISO week and staff member for the Weekly Summary report.
   * Accepts locale so the week label string is pre-computed and stored on each row.
   * Returns rows sorted by week number, then staff name.
   */
  _groupScheduleByWeek(data, locale = 'en') {
    const weeks = {};
    data.forEach(entry => {
      const weekNum = entry.weekNumber || 0;
      if (!weeks[weekNum]) {
        weeks[weekNum] = {};
      }
      const staffKey = entry.staffId || entry.staffName || 'unknown';
      if (!weeks[weekNum][staffKey]) {
        weeks[weekNum][staffKey] = {
          weekNumber: weekNum,
          weekLabel: locale === 'de' ? `KW ${weekNum}` : `Week ${weekNum}`,
          staffName: entry.staffName || '',
          totalMinutes: 0
        };
      }
      weeks[weekNum][staffKey].totalMinutes += (entry.totalMinutes || 0);
    });

    const rows = [];
    Object.keys(weeks)
      .sort((a, b) => Number(a) - Number(b))
      .forEach(weekNum => {
        Object.values(weeks[weekNum])
          .sort((a, b) => (a.staffName || '').localeCompare(b.staffName || ''))
          .forEach(row => rows.push(row));
      });
    return rows;
  }

  // ─── Title and column configuration ───────────────────────────────────────

  /**
   * Returns a translated report title for all report types.
   */
  getReportTitle(reportType, t) {
    const titleMap = {
      'Working Time Record':  t('workingTimeRecord'),
      'All Time Entries':     t('allTimeEntries'),
      'Daily Summary':        t('dailySummary'),
      'Vacation Overview':    t('vacationOverview'),
      'Vacation Summary':     t('vacationSummary'),
      'Schedule Overview':    t('scheduleOverview'),
      'Weekly Summary':       t('weeklyScheduleSummary'),
      'Sick Leave Overview':  t('sickLeaveOverview')
    };
    return titleMap[reportType] || reportType;
  }

  /**
   * Returns column definitions (label + style) for all report types.
   *
   * Working Time Record: one row per staff per day with 7 columns (date, staff,
   * first clock-in, last clock-out, presence, break, work). Uses the wtr*
   * styles so the staff column keeps its paddingRight (col(i) only reads
   * `style`, a bare `width` would drop it).
   *
   * All Time Entries: the value column uses t('presenceDuration') —
   * "Presence" (EN) / "Anwesenheit" (DE) — because each row now shows the
   * raw clockOut − clockIn span, not net work time. Net work is Daily
   * Summary's role.
   *
   * Vacation and sick leave types use 2-column layouts only — no
   * duration/hours columns.
   */
  getColumnConfigs(t) {
    return {
      // ── Time Tracking ────────────────────────────────────────────────────────
      'Working Time Record': [
        { label: t('date'),             style: styles.wtrDateCell },
        { label: t('staff'),            style: styles.wtrStaffCell },
        { label: t('clockIn'),          style: styles.wtrTimeCell },
        { label: t('clockOut'),         style: styles.wtrTimeCell },
        { label: t('presenceDuration'), style: styles.wtrPresenceCell },
        { label: t('breakTime'),        style: styles.wtrBreakCell },
        { label: t('workDuration'),     style: styles.wtrWorkCell }
      ],
      // presenceDuration key maps to "Presence" (EN) / "Anwesenheit" (DE),
      // matching what column 4 now shows: raw clockOut − clockIn per session.
      'All Time Entries': [
        { label: t('date'),             style: styles.dateCell },
        { label: t('staff'),            style: styles.staffCell },
        { label: t('startTime'),        width: '14%' },
        { label: t('endTime'),          width: '14%' },
        { label: t('presenceDuration'), width: '28%' }
      ],
      'Daily Summary': [
        { label: t('date'),              style: styles.dateCell },
        { label: t('staff'),             style: styles.staffCell },
        { label: t('presenceDuration'),  style: styles.presenceDurationCell },
        { label: t('breakTime'),         style: styles.breakTimeCell },
        { label: t('workDuration'),      style: styles.workDurationCell }
      ],

      // ── Vacation ─────────────────────────────────────────────────────────────
      'Vacation Overview': [
        { label: t('date'),          style: styles.vacDateCell },
        { label: t('staff'),         style: styles.vacStaffCell }
      ],
      'Vacation Summary': [
        { label: t('staff'),         style: styles.vacSummStaffCell },
        { label: t('vacationDays'),  style: styles.vacSummDaysCell }
      ],

      // ── Schedule ─────────────────────────────────────────────────────────────
      'Schedule Overview': [
        { label: t('date'),           style: styles.schedDateCell },
        { label: t('staff'),          style: styles.schedStaffCell },
        { label: t('shiftStart'),     style: styles.schedStartCell },
        { label: t('shiftEnd'),       style: styles.schedEndCell },
        { label: t('scheduledHours'), style: styles.schedHoursCell }
      ],
      // 'weekLabel' contains {{week}} for row-level interpolation and must not
      // be used as a static header label. 'calendarWeek' is a plain string key.
      'Weekly Summary': [
        { label: t('calendarWeek'),   style: styles.weekLabelCell },
        { label: t('staff'),          style: styles.weekStaffCell },
        { label: t('scheduledHours'), style: styles.weekHoursCell }
      ],

      // Sick Leave Overview — same 2-column layout as Vacation Overview.
      // Reuses vacDateCell and vacStaffCell; no new StyleSheet entries needed.
      // durationMinutes is excluded per v1 days-only decision.
      'Sick Leave Overview': [
        { label: t('date'),   style: styles.vacDateCell },
        { label: t('staff'),  style: styles.vacStaffCell }
      ]
    };
  }

  // ─── Main entry point ──────────────────────────────────────────────────────

  /**
   * Builds the full @react-pdf/renderer Document tree for any report type.
   *
   * reportCategory is optional - it is inferred from reportType when absent
   * so existing callers that do not pass it continue to work.
   */
  generatePDF({
    title,
    reportType,
    reportCategory,
    startDate,
    endDate,
    data,
    companyName,
    departmentName,
    t,
    locale = 'en'
  }) {
    const columnConfigs = this.getColumnConfigs(t);
    const columns = columnConfigs[reportType] || columnConfigs['Daily Summary'];

    const effectiveCategory = reportCategory || inferCategory(reportType);

    // Resolve the translated report type label separately so it can be
    // passed independently to _renderHeader and displayed on its own line,
    // preventing the company name and report type from concatenating into one
    // long string that wraps mid-word on the PDF page.
    const reportTypeLabel = this.getReportTitle(reportType, t);

    let processedData = data;
    if (reportType === 'Vacation Summary') {
      processedData = this._groupVacationByStaff(data);
    } else if (reportType === 'Weekly Summary') {
      processedData = this._groupScheduleByWeek(data, locale);
    }

    let totals = {};

    if (effectiveCategory === 'vacation') {
      if (reportType === 'Vacation Summary') {
        totals = {
          totalDays: processedData.reduce((sum, e) => sum + (e.totalDays || 0), 0)
        };
      } else {
        totals = {
          totalDays: data.length
        };
      }
    } else if (effectiveCategory === 'schedule') {
      totals = {
        totalScheduleMinutes: processedData.reduce(
          (sum, e) => sum + (e.totalMinutes || 0), 0
        )
      };
    } else if (effectiveCategory === 'sick-leave') {
      // Days-only totals — mirrors vacation overview branch.
      // durationMinutes is available on each entry but excluded per v1 decision.
      totals = {
        totalDays: data.length
      };
    } else {
      // Time tracking (Working Time Record, All Time Entries, Daily Summary).
      // totalPresence is used by All Time Entries totals row;
      // totalPresence, totalBreak and totalWork are used by the Working Time
      // Record and Daily Summary totals rows.
      totals = {
        totalPresence: data.reduce((sum, e) => sum + (e.presenceDuration || 0), 0),
        totalBreak:    data.reduce((sum, e) => sum + (e.breakDuration    || 0), 0),
        totalWork:     data.reduce((sum, e) => sum + (e.workDuration     || 0), 0)
      };
    }

    const staffMembers = [...new Set(data.map(e => e.staffName).filter(Boolean))];
    const primaryStaff = staffMembers.length === 1 ? staffMembers[0] : null;

    return (
      <Document>
        {this._renderPages({
          reportTypeLabel,
          reportType,
          startDate,
          endDate,
          data: processedData,
          companyName,
          departmentName,
          columns,
          totals,
          primaryStaff,
          t,
          locale
        })}
      </Document>
    );
  }

  // ─── Page rendering ────────────────────────────────────────────────────────

  /**
   * Splits data across A4 pages and assembles each Page component.
   */
  _renderPages({
    reportTypeLabel, reportType, startDate, endDate, data,
    companyName, departmentName, columns, totals,
    primaryStaff, t, locale
  }) {
    const ITEMS_PER_PAGE = 25;
    const totalPages = Math.max(1, Math.ceil(data.length / ITEMS_PER_PAGE));
    const pages = [];

    for (let i = 0; i < totalPages; i++) {
      const pageData = data.slice(i * ITEMS_PER_PAGE, (i + 1) * ITEMS_PER_PAGE);

      pages.push(
        <Page key={`page-${i}`} size="A4" style={styles.page}>
          <Text style={styles.pageNumber}>
            {t('pageNumber', { current: i + 1, total: totalPages })}
          </Text>

          {this._renderHeader({
            reportTypeLabel,
            reportType,
            startDate,
            endDate,
            companyName,
            departmentName,
            primaryStaff,
            t,
            locale
          })}

          {this._renderTable({
            columns,
            data: pageData,
            reportType,
            showTotals: i === totalPages - 1,
            totals,
            t
          })}

          {this._renderFooter(t, locale)}
        </Page>
      );
    }

    return pages;
  }

  // ─── Header ────────────────────────────────────────────────────────────────

  // Accepts companyName and reportTypeLabel separately.
  // Renders the company name in a smaller secondary style on its own line,
  // followed by the report type title on its own line at a comfortable size.
  // This prevents the two strings from merging into one long wrapping block.
  _renderHeader({
    reportTypeLabel,
    startDate,
    endDate,
    companyName,
    departmentName,
    primaryStaff,
    t,
    locale
  }) {
    const formatPeriodDate = (date) => {
      const day   = date.getDate();
      const month = date.toLocaleString(
        locale === 'de' ? 'de-DE' : 'en-US',
        { month: 'short' }
      );
      const year  = date.getFullYear();
      return `${day}. ${month} ${year}`;
    };

    return (
      <View style={styles.header}>
        {/* Company name in a modest secondary line above the report title */}
        {companyName && (
          <Text style={styles.companyTitle}>{companyName}</Text>
        )}

        {/* Report type on its own line - not concatenated with company name */}
        <Text style={styles.reportTypeTitle}>{reportTypeLabel}</Text>

        {primaryStaff ? (
          <>
            <Text style={styles.staffName}>{primaryStaff}</Text>
            <Text style={styles.departmentLine}>
              {t('department')}: {departmentName}
            </Text>
          </>
        ) : (
          <Text style={styles.staffName}>{departmentName}</Text>
        )}

        <View style={styles.section}>
          <Text style={styles.sectionLabel}>{t('reportPeriod')}:</Text>
          <Text style={styles.sectionValue}>
            {formatPeriodDate(startDate)} {t('to')} {formatPeriodDate(endDate)}
          </Text>
        </View>

        <View style={styles.divider} />
      </View>
    );
  }

  // ─── Table ─────────────────────────────────────────────────────────────────

  _renderTable({ columns, data, reportType, showTotals, totals, t }) {
    return (
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          {columns.map((column, index) => (
            <Text
              key={`header-${index}`}
              style={[styles.headerCell, column.style || { width: column.width }]}
            >
              {column.label}
            </Text>
          ))}
        </View>

        {data.map((entry, rowIndex) => (
          <View
            key={`row-${rowIndex}`}
            style={[
              styles.tableRow,
              rowIndex % 2 === 1 ? styles.alternateRow : null
            ]}
          >
            {this._renderRowCells(entry, columns, reportType, t)}
          </View>
        ))}

        {showTotals && this._renderTotalsRow(reportType, totals, columns, t)}
      </View>
    );
  }

  // ─── Row cells ─────────────────────────────────────────────────────────────

  /**
   * Returns the cell array for a single data row.
   * Each case maps directly to one of the report types.
   *
   * All Time Entries: column 4 shows presenceDuration (clockOut − clockIn)
   * so the value ties exactly to the Start/End columns on the same row.
   * Net work time is the job of Daily Summary.
   */
  _renderRowCells(entry, columns, reportType, t) {
    const col = (i) => columns[i].style || { width: columns[i].width };

    switch (reportType) {

      case 'Working Time Record':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{this._formatShortDate(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>,
          <Text key="c2" style={[styles.cell, col(2)]}>{formatTime(entry.clockIn)}</Text>,
          <Text key="c3" style={[styles.cell, col(3)]}>{formatTime(entry.clockOut)}</Text>,
          <Text key="c4" style={[styles.cell, col(4)]}>{formatDuration(entry.presenceDuration)}</Text>,
          <Text key="c5" style={[styles.cell, col(5)]}>{formatDuration(entry.breakDuration)}</Text>,
          <Text key="c6" style={[styles.cell, col(6)]}>{formatDuration(entry.workDuration)}</Text>
        ];

      case 'All Time Entries':
        // Column 4 is presence (clockOut − clockIn) — matches the Start/End
        // columns shown on this row. Net work time is Daily Summary's role.
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{this._formatShortDate(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>,
          <Text key="c2" style={[styles.cell, col(2)]}>{formatTime(entry.clockIn)}</Text>,
          <Text key="c3" style={[styles.cell, col(3)]}>{formatTime(entry.clockOut)}</Text>,
          <Text key="c4" style={[styles.cell, col(4)]}>{formatDuration(entry.presenceDuration)}</Text>
        ];

      case 'Daily Summary':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{this._formatShortDate(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>,
          <Text key="c2" style={[styles.cell, col(2)]}>{formatDuration(entry.presenceDuration)}</Text>,
          <Text key="c3" style={[styles.cell, col(3)]}>{formatDuration(entry.breakDuration)}</Text>,
          <Text key="c4" style={[styles.cell, col(4)]}>{formatDuration(entry.workDuration)}</Text>
        ];

      case 'Vacation Overview':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{formatDateFromString(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>
        ];

      case 'Vacation Summary':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{entry.staffName || ''}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{String(entry.totalDays || 0)}</Text>
        ];

      case 'Schedule Overview':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{formatDateFromString(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>,
          <Text key="c2" style={[styles.cell, col(2)]}>{entry.startTime || ''}</Text>,
          <Text key="c3" style={[styles.cell, col(3)]}>{entry.endTime   || ''}</Text>,
          <Text key="c4" style={[styles.cell, col(4)]}>{formatMinutes(entry.totalMinutes)}</Text>
        ];

      // entry is pre-grouped from _groupScheduleByWeek: { weekLabel, staffName, totalMinutes }
      // weekLabel is pre-computed as "KW 6" (de) or "Week 6" (en) — no translation needed here
      case 'Weekly Summary':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{entry.weekLabel || ''}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>,
          <Text key="c2" style={[styles.cell, col(2)]}>{formatMinutes(entry.totalMinutes)}</Text>
        ];

      // Sick Leave Overview row — date string + staff name, no duration.
      // Uses formatDateFromString to avoid UTC midnight timezone shift.
      case 'Sick Leave Overview':
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{formatDateFromString(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>
        ];

      default:
        return [
          <Text key="c0" style={[styles.cell, col(0)]}>{this._formatShortDate(entry.date)}</Text>,
          <Text key="c1" style={[styles.cell, col(1)]}>{entry.staffName || ''}</Text>,
          <Text key="c2" style={[styles.cell, col(2)]}>{formatDuration(entry.presenceDuration || 0)}</Text>,
          <Text key="c3" style={[styles.cell, col(3)]}>{formatDuration(entry.breakDuration    || 0)}</Text>,
          <Text key="c4" style={[styles.cell, col(4)]}>{formatDuration(entry.workDuration     || 0)}</Text>
        ];
    }
  }

  // ─── Totals row ────────────────────────────────────────────────────────────

  /**
   * All Time Entries: totals cell shows totalPresence so the grand total
   * matches the Daily Summary Anwesenheit column for the same period
   * (110:17 in the example, not 103:32).
   */
  _renderTotalsRow(reportType, totals, columns, t) {
    const col = (i) => columns[i].style || { width: columns[i].width };

    switch (reportType) {

      case 'Working Time Record':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{t('allStaff')}</Text>
            <Text style={[styles.totalCell, col(2)]}></Text>
            <Text style={[styles.totalCell, col(3)]}></Text>
            <Text style={[styles.totalCell, col(4)]}>{formatDuration(totals.totalPresence)}</Text>
            <Text style={[styles.totalCell, col(5)]}>{formatDuration(totals.totalBreak)}</Text>
            <Text style={[styles.totalCell, col(6)]}>{formatDuration(totals.totalWork)}</Text>
          </View>
        );

      case 'All Time Entries':
        // Grand total uses totalPresence so it aligns with the Daily Summary
        // Anwesenheit total for the same period. totalWork is not shown here.
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{t('allStaff')}</Text>
            <Text style={[styles.totalCell, col(2)]}></Text>
            <Text style={[styles.totalCell, col(3)]}></Text>
            <Text style={[styles.totalCell, col(4)]}>{formatDuration(totals.totalPresence)}</Text>
          </View>
        );

      case 'Daily Summary':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{t('allStaff')}</Text>
            <Text style={[styles.totalCell, col(2)]}>{formatDuration(totals.totalPresence)}</Text>
            <Text style={[styles.totalCell, col(3)]}>{formatDuration(totals.totalBreak)}</Text>
            <Text style={[styles.totalCell, col(4)]}>{formatDuration(totals.totalWork)}</Text>
          </View>
        );

      case 'Vacation Overview':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>
              {`${totals.totalDays} ${t('vacationDays')}`}
            </Text>
          </View>
        );

      case 'Vacation Summary':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{String(totals.totalDays || 0)}</Text>
          </View>
        );

      case 'Schedule Overview':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{t('allStaff')}</Text>
            <Text style={[styles.totalCell, col(2)]}></Text>
            <Text style={[styles.totalCell, col(3)]}></Text>
            <Text style={[styles.totalCell, col(4)]}>{formatMinutes(totals.totalScheduleMinutes)}</Text>
          </View>
        );

      case 'Weekly Summary':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{t('allStaff')}</Text>
            <Text style={[styles.totalCell, col(2)]}>{formatMinutes(totals.totalScheduleMinutes)}</Text>
          </View>
        );

      // Sick Leave Overview totals — 2-cell layout matching the column config.
      // Uses t('sickDays') instead of t('vacationDays') to distinguish the label
      // while keeping the same structural pattern.
      case 'Sick Leave Overview':
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>
              {`${totals.totalDays} ${t('sickDays')}`}
            </Text>
          </View>
        );

      default:
        return (
          <View style={styles.totalRow}>
            <Text style={[styles.totalCell, col(0)]}>{t('total')}</Text>
            <Text style={[styles.totalCell, col(1)]}>{t('allStaff')}</Text>
            <Text style={[styles.totalCell, col(2)]}>{formatDuration(totals.totalPresence || 0)}</Text>
            <Text style={[styles.totalCell, col(3)]}>{formatDuration(totals.totalBreak    || 0)}</Text>
            <Text style={[styles.totalCell, col(4)]}>{formatDuration(totals.totalWork     || 0)}</Text>
          </View>
        );
    }
  }

  // ─── Footer ────────────────────────────────────────────────────────────────

  _renderFooter(t, locale) {
    const formatTimestamp = () => {
      const now     = new Date();
      const day     = now.getDate();
      const month   = now.toLocaleString(
        locale === 'de' ? 'de-DE' : 'en-US',
        { month: 'short' }
      );
      const year    = now.getFullYear();
      const hours   = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, '0');
      return `${day}. ${month} ${year} ${t('at')} ${hours}:${minutes}`;
    };

    return (
      <View style={styles.footer}>
        <Text>{t('generatedBy', { timestamp: formatTimestamp() })}</Text>
        <Text style={styles.companyInfo}>{t('klokifyTagline')}</Text>
      </View>
    );
  }

  // ─── Date formatting helper ────────────────────────────────────────────────

  _formatShortDate(date) {
    if (!date) return '';
    if (typeof date === 'string') return formatDateFromString(date);
    const day   = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year  = date.getFullYear().toString().slice(-2);
    return `${day}.${month}.${year}`;
  }

  // ─── Public download method ────────────────────────────────────────────────

  async downloadPDF(pdfProps, fileName) {
    try {
      const pdfDoc = this.generatePDF(pdfProps);
      const blob   = await pdf(pdfDoc).toBlob();

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        const url  = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href     = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 100);
      }

      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }
}

export default new PDFGeneratorService();